import { useFormState } from '@/forms/core';
import type { FieldsGroupRestrictionPayload } from '@/forms/typings';
import { atLeastOneChildAnsweredYesKey } from '@/forms/utils/createAtLeastOneChildAnsweredYesYup';
import { atLeastOneChildWithValueKey } from '@/forms/utils/createAtLeastOneChildWithValueYup';
import { limitRuleKey } from '@/forms/utils/createLimitPairsYup';
import { requiredWhenValueKey } from '@/forms/utils/createRequiredWhenValueYup';

/**
 * True when any restriction on the group has a touched validation error. Pure
 * so it can be unit tested without a Formik/React render tree.
 */
export const hasGroupRestrictionError = (
  errors: Record<string, string | undefined>,
  touched: Record<string, boolean>,
  groupFieldId: string,
  restriction?: FieldsGroupRestrictionPayload,
): boolean => {
  if (!restriction) return false;

  const keys: string[] = [];

  if (restriction.atLeastOneChildWithValue)
    keys.push(atLeastOneChildWithValueKey(groupFieldId));

  if (restriction.atLeastOneChildAnsweredYes)
    keys.push(atLeastOneChildAnsweredYesKey(groupFieldId));

  if (restriction.requiredWhenValue)
    keys.push(requiredWhenValueKey(groupFieldId));

  if (restriction.limitPairs)
    for (const pair of restriction.limitPairs.pairs)
      for (const itemID of [pair.lowerItemID, pair.upperItemID])
        keys.push(limitRuleKey(`${groupFieldId}_${itemID}`));

  return keys.some(key => Boolean(errors[key]) && Boolean(touched[key]));
};

/**
 * True when any restriction on the group has a touched validation error. Single
 * dispatch point — adding a new restriction kind only extends this.
 */
export const useGroupRestrictionError = (
  groupFieldId: string,
  restriction?: FieldsGroupRestrictionPayload,
): boolean => {
  const { errors, touched } = useFormState();

  return hasGroupRestrictionError(errors, touched, groupFieldId, restriction);
};
